import { isInMatch } from '../machine';
import { markersAreFinite } from '../setPiece';
import { ok, reject } from '../result';
import type {
  CaptureSetPieceCommand,
  CaptureSetPieceSnapshot,
  CloseSetPieceCommand,
  CloseSetPieceSnapshot,
  Decision,
  SetPieceOp,
} from './types';

/**
 * SP-1 … SP-6 — the set-piece capture handlers (build unit U-12).
 *
 * ── `Decision<SetPieceOp>` is SP-1's mechanism, and it is a compile error ────
 *
 * SP-1: *"Entered and exited from the in-play top zone **without disturbing capture phase, match
 * state, or any tile position**."* §9 offers *"no Command is dispatched on entry or exit"* as the
 * argument, and §18's traceability row for SP-1 is explicit that this is not the acceptance — the
 * acceptance is that `Match.state`, `capturePhase` and *"every tile's `boardPosition` and rendered
 * slot"* are observably unchanged, *"not the 'no Command dispatched' proxy"*.
 *
 * Entry and exit dispatch nothing, so that half is trivially true. What has to hold for the other
 * half is that a capture **inside** the sheet cannot disturb them either, and that is what
 * {@link SetPieceOp} makes unspellable: `setBoardPositions`, `confirmLock`, `transitionMatch`,
 * `patchMatchSetup`, `writeCountState`, `insertObservation` and `deleteIncrement` are all absent from
 * it, so no handler in this file can compile one. The behavioural assertions in the test suite and
 * the browser pass are the acceptance; this type is why they cannot start failing later.
 *
 * ── Draft persistence is commit-on-arrival ──────────────────────────────────
 *
 * §2.5: *"Set-piece captures are persisted as **draft rows updated on every marker tap** … so a
 * process kill mid-capture loses nothing."* Each tap is its own command and its own transaction. The
 * alternative — accumulate in memory and save at Done — is the shape that loses the whole capture to
 * a backgrounded tab being purged, which on iOS is an ordinary event and not a fault.
 */

const MODULE = 'commands/setPiece';

/**
 * SP-2 … SP-5 — validate and write the capture as it stands after this tap.
 *
 * The window is **in-match**, the same predicate LIVE-6's undo window and AUD-2's voice note use.
 * `preMatch` is outside it: PRE-7's exits hand the user a capture surface with the clock unstarted
 * (§6.0), and a corner captured there would be stamped `matchMinute: 0` for a match that has not
 * kicked off — §6.4a's negative for a tap on a tile, one surface over. `fullTime` and beyond are
 * outside it because there is no half left for a set piece to have happened in.
 */
export function handleCaptureSetPiece(
  cmd: CaptureSetPieceCommand,
  snap: CaptureSetPieceSnapshot,
): Decision<SetPieceOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (!isInMatch(match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_MATCH_NOT_CAPTURING',
      'Set pieces are captured while the match is underway.',
      { state: match.state, id: cmd.row.id },
    );
  }

  if (cmd.row.matchId !== cmd.matchId) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'That capture belongs to a different match.',
      { id: cmd.row.id, rowMatchId: cmd.row.matchId },
    );
  }

  // §9 — the row this command writes is a *draft*. A `draft: false` upsert would be the second way to
  // finish a capture, and `CloseSetPiece` is the first; two exits is how one of them ends up
  // forgotten. Closing goes through the command named for it.
  if (!cmd.row.draft) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_SET_PIECE_CLOSED',
      'A finished capture cannot be rewritten. Start a new one.',
      { id: cmd.row.id },
    );
  }

  if (!markersAreFinite(cmd.row.markers)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_SET_PIECE_GEOMETRY',
      'That marker has no position on the diagram.',
      { id: cmd.row.id, markerCount: cmd.row.markers.length },
    );
  }

  const existing = snap.existing;
  if (existing !== undefined) {
    if (existing.matchId !== cmd.matchId) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_COMMAND_ROW_MISMATCH',
        'That capture id already belongs to a different match.',
        { id: cmd.row.id, existingMatchId: existing.matchId },
      );
    }

    if (!existing.draft) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_SET_PIECE_CLOSED',
        'That capture was already finished. Start a new one.',
        { id: cmd.row.id },
      );
    }

    // SP-4's *"at the moment the draft is created"*, and SP-2/SP-3's marker vocabularies, made
    // properties of the stored row rather than of the surface resending the same values.
    if (existing.matchMinute !== cmd.row.matchMinute || existing.type !== cmd.row.type) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_SET_PIECE_IMMUTABLE',
        'A capture keeps the minute and the kind it was started with.',
        {
          id: cmd.row.id,
          minute: { was: existing.matchMinute, sent: cmd.row.matchMinute },
          type: { was: existing.type, sent: cmd.row.type },
        },
      );
    }
  }

  return ok({
    ops: [{ t: 'upsertSetPiece', row: cmd.row }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        // Logged at every tap on purpose: §2.5's promise is that a kill loses nothing, and the OBS-1
        // ring is where a user who lost a capture would find out whether the write ever happened.
        msg: existing === undefined ? 'SP-4: set-piece draft created' : 'SP-5: set-piece draft written',
        data: {
          id: cmd.row.id,
          type: cmd.row.type,
          matchMinute: cmd.row.matchMinute,
          markerCount: cmd.row.markers.length,
        },
      },
    ],
    post: [],
  });
}

/**
 * §9 — *"Exiting the sheet clears `draft`."*
 *
 * **Idempotent, and deliberately not state-gated.** Closing a capture that has vanished, or one that
 * is already closed, succeeds with no ops and a `warn` line rather than a rejection: the user's Done
 * tap must complete either way, and a `[Done]` that reported an error because the row had already been
 * closed would be a dead end on a live surface. Neither case is silent — see {@link handleCaptureSetPiece}
 * for why the state gate is absent.
 */
export function handleCloseSetPiece(
  cmd: CloseSetPieceCommand,
  snap: CloseSetPieceSnapshot,
): Decision<SetPieceOp> {
  const existing = snap.existing;

  if (existing === undefined) {
    return ok({
      ops: [],
      log: [
        {
          module: MODULE,
          level: 'warn',
          matchId: cmd.matchId,
          msg: 'SP-1: the set-piece capture vanished before it could be closed',
          data: { id: cmd.setPieceId },
        },
      ],
      post: [],
    });
  }

  if (existing.matchId !== cmd.matchId) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'That capture belongs to a different match.',
      { id: cmd.setPieceId, existingMatchId: existing.matchId },
    );
  }

  if (!existing.draft) {
    return ok({
      ops: [],
      log: [
        {
          module: MODULE,
          level: 'info',
          matchId: cmd.matchId,
          msg: 'SP-1: the set-piece capture was already closed',
          data: { id: cmd.setPieceId },
        },
      ],
      post: [],
    });
  }

  return ok({
    ops: [{ t: 'closeSetPieceDraft', id: cmd.setPieceId }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'SP-1: set-piece capture closed',
        data: { id: cmd.setPieceId, type: existing.type, markerCount: existing.markers.length },
      },
    ],
    post: [],
  });
}
